<template>
  <div class="hls-video-wrapper" :style="{ width: width, height: height }">
    <video
      ref="videoRef"
      class="hls-video"
      :muted="muted"
      :autoplay="autoplay"
      :controls="controls"
      :loop="loop"
      :poster="poster"
      playsinline
      webkit-playsinline
      x5-playsinline
      x5-video-player-type="h5"
    ></video>
    <div v-if="loading && !playing" class="hls-video-loading">
      <div class="loading-spinner"></div>
    </div>
    <div v-if="error" class="hls-video-error">
      <span>{{ error }}</span>
      <button v-if="retryable" class="retry-btn" @click="retry">重试</button>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js'

export default {
  name: 'HlsVideo',
  props: {
    src: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '100%',
    },
    muted: {
      type: Boolean,
      default: true,
    },
    autoplay: {
      type: Boolean,
      default: true,
    },
    controls: {
      type: Boolean,
      default: true,
    },
    loop: {
      type: Boolean,
      default: true,
    },
    poster: {
      type: String,
      default: '',
    },
    retryable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hls: null,
      loading: false,
      playing: false,
      error: '',
      reinitCount: 0,
      maxReinits: 3,
      stuckTimer: null,
      lastProgress: 0,
    }
  },
  watch: {
    src(newVal) {
      if (newVal) {
        this.destroyHls()
        this.reinitCount = 0
        this.initHls()
      }
    },
  },
  mounted() {
    if (this.src) {
      this.initHls()
    }
  },
  beforeDestroy() {
    this.destroyHls()
  },
  methods: {
    initHls() {
      const video = this.$refs.videoRef
      if (!video || !this.src) return

      this.loading = true
      this.playing = false
      this.error = ''
      this.lastProgress = Date.now()

      if (Hls.isSupported()) {
        this.hls = new Hls({
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          enableWorker: true,
          lowLatencyMode: false,
          liveSyncDurationCount: 3,
          liveMaxLatencyDurationCount: 10,
          // 片段加载失败时多试几次，避免偶尔 404 就报错
          fragLoadingMaxRetry: 6,
          fragLoadingMaxRetryTimeout: 64000,
          fragLoadingTimeOut: 20000,
          // manifest 加载重试
          manifestLoadingMaxRetry: 4,
          manifestLoadingMaxRetryTimeout: 10000,
          manifestLoadingTimeOut: 10000,
          // level 加载重试
          levelLoadingMaxRetry: 4,
          levelLoadingMaxRetryTimeout: 10000,
          levelLoadingTimeOut: 10000,
        })

        this.hls.loadSource(this.src)
        this.hls.attachMedia(video)

        this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
          this.loading = false
          this.playing = true
          this.error = ''
          this.lastProgress = Date.now()
          this.startStuckCheck()
          if (this.autoplay) {
            video.play().catch(() => {})
          }
        })

        this.hls.on(Hls.Events.FRAG_LOADED, () => {
          this.lastProgress = Date.now()
          if (this.loading && !this.playing) {
            this.loading = false
            this.playing = true
            this.error = ''
            this.startStuckCheck()
          }
        })

        this.hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return

          console.warn('[HlsVideo] fatal error:', data.type, data.details)
          this.lastProgress = Date.now()

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              // 网络错误：静默重试，不显示错误
              this.hls.startLoad()
              break
            case Hls.ErrorTypes.MEDIA_ERROR:
              // 媒体错误：尝试恢复
              this.hls.recoverMediaError()
              break
            default:
              this.destroyHls()
              this.reinitOrFail('播放失败')
              break
          }
        })
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = this.src
        video.addEventListener('loadedmetadata', () => {
          this.loading = false
          this.playing = true
          if (this.autoplay) {
            video.play().catch(() => {})
          }
        })
        video.addEventListener('error', () => {
          this.loading = false
          this.reinitOrFail('播放失败')
        })
      } else {
        this.loading = false
        this.error = '当前浏览器不支持HLS播放'
      }
    },

    // 卡住检测：15 秒无进展则重建
    startStuckCheck() {
      this.stopStuckCheck()
      this.stuckTimer = setInterval(() => {
        if (Date.now() - this.lastProgress > 15000) {
          console.warn('[HlsVideo] stuck for 15s, reinit')
          this.stopStuckCheck()
          this.destroyHls()
          this.reinitOrFail('视频流加载超时')
        }
      }, 3000)
    },

    stopStuckCheck() {
      if (this.stuckTimer) {
        clearInterval(this.stuckTimer)
        this.stuckTimer = null
      }
    },

    reinitOrFail(errMsg) {
      this.reinitCount++
      if (this.reinitCount <= this.maxReinits) {
        console.log('[HlsVideo] reinit attempt', this.reinitCount)
        this.loading = true
        this.playing = false
        setTimeout(() => this.initHls(), 2000)
      } else {
        this.loading = false
        this.playing = false
        this.error = errMsg || '播放失败'
      }
    },

    destroyHls() {
      this.stopStuckCheck()
      if (this.hls) {
        this.hls.destroy()
        this.hls = null
      }
    },

    retry() {
      this.error = ''
      this.reinitCount = 0
      this.destroyHls()
      this.initHls()
    },

    play() {
      this.$refs.videoRef && this.$refs.videoRef.play()
    },

    pause() {
      this.$refs.videoRef && this.$refs.videoRef.pause()
    },
  },
}
</script>

<style lang="scss" scoped>
.hls-video-wrapper {
  position: relative;
  overflow: hidden;
  background: #000;
  border-radius: 4px;

  .hls-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .hls-video-loading,
  .hls-video-error {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    background: rgba(0, 0, 0, 0.5);
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid #fff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .retry-btn {
    margin-top: 10px;
    padding: 4px 16px;
    border: 1px solid #fff;
    border-radius: 4px;
    background: transparent;
    color: #fff;
    cursor: pointer;
    font-size: 13px;

    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  }
}
</style>
